import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Input } from '@fiado/react-kit';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { handleApiError } from '../../api/errorMapping';
import { useTenantUserFields } from '../../hooks/useTenantUserFields';
import { DynamicUserFields } from '../scoped/DynamicUserFields';
import { buildCustomFieldsSchema, pickCustomFieldsDefaults } from '../scoped/userFieldsSchema';
import type {
    TenantUserDetailLocal,
    UpdateTenantUserRequestLocal,
} from '../../api/types/TenantLocal';
import { requiredNameField, optionalPhoneField } from '../../lib/formValidators';

const baseSchema = z.object({
    displayName: requiredNameField,
    phone: optionalPhoneField,
});

interface FormValues extends z.infer<typeof baseSchema> {
    customFields?: Record<string, string>;
}

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Email/nombre del usuario (de la fila) para el header y el default mientras carga el detalle. */
    userEmail: string;
    userDisplayName: string;
    /** Tenant dueño del catálogo de campos personalizados y de las asignaciones. */
    tenantId: string;
    /** Trae el detalle (con phone + customFields actuales) para precargar el form. */
    getDetail: () => Promise<TenantUserDetailLocal>;
    /** queryKey del detalle (compartida con el resto del menú, cachea el mismo GET). */
    detailQueryKey: QueryKey;
    /** Ejecuta el update real (binding del flujo: tenant/retail/platform). */
    update: (body: UpdateTenantUserRequestLocal) => Promise<unknown>;
    /** Invalida el listado tras guardar OK. */
    onSaved: () => Promise<void> | void;
}

/**
 * Dialog de edición de usuario con campos personalizados del tenant. Usa react-hook-form (los `<Select>`
 * del kit que rinde `DynamicUserFields` son controlados → necesitan `control`). Precarga
 * displayName/phone/customFields desde el detalle (`getDetail`), que ahora trae `customFields`.
 *
 * El catálogo se filtra a enabled; si el tenant no tiene catálogo, no se rinde ninguna sección extra.
 */
export function EditUserDialog({
    open,
    onOpenChange,
    userEmail,
    userDisplayName,
    tenantId,
    getDetail,
    detailQueryKey,
    update,
    onSaved,
}: Props) {
    const { data: customFields = [] } = useTenantUserFields(tenantId, { enabledOnly: true });
    // Detalle SOLO al abrir el dialog (precarga phone + customFields actuales). Cachea bajo la misma
    // queryKey del detalle compartida por el menú.
    const {
        data: detail,
        isError: detailError,
        refetch: refetchDetail,
    } = useQuery({
        queryKey: detailQueryKey,
        queryFn: getDetail,
        enabled: open,
    });

    const schema = useMemo(
        () => baseSchema.extend({ customFields: buildCustomFieldsSchema(customFields) }),
        [customFields],
    );

    // Defaults reactivos: arrancan de la fila; cuando llega el detalle + catálogo se re-siembran.
    const values = useMemo<FormValues>(
        () => ({
            displayName: detail?.displayName ?? userDisplayName,
            phone: detail?.phone ?? '',
            customFields: pickCustomFieldsDefaults(customFields, detail?.customFields),
        }),
        [detail, userDisplayName, customFields],
    );

    const form = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: { displayName: userDisplayName, phone: '', customFields: {} },
        values,
        resetOptions: { keepDirtyValues: true },
    });

    const updateMut = useMutation({
        mutationFn: (v: FormValues) =>
            update({
                displayName: v.displayName.trim(),
                phone: v.phone?.trim() || undefined,
                // Solo si el tenant tiene catálogo (mapa no vacío); si no, se omite del body.
                customFields:
                    v.customFields && Object.keys(v.customFields).length > 0
                        ? v.customFields
                        : undefined,
            }),
        onSuccess: async () => {
            toast.success('Usuario actualizado');
            onOpenChange(false);
            await onSaved();
        },
        onError: handleApiError,
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Editar usuario</DialogTitle>
                    <DialogDescription>{userEmail}</DialogDescription>
                </DialogHeader>
                <form
                    onSubmit={(e) => {
                        void form.handleSubmit((v) => {
                            updateMut.mutate(v);
                        })(e);
                    }}
                    className="space-y-3"
                >
                    <Input
                        label="Nombre"
                        error={form.formState.errors.displayName?.message}
                        {...form.register('displayName')}
                    />
                    <Input
                        label="Teléfono"
                        type="tel"
                        inputMode="tel"
                        placeholder="+52..."
                        hint="10 dígitos, o +52 con código de país"
                        error={form.formState.errors.phone?.message}
                        {...form.register('phone')}
                    />

                    {detailError && (
                        <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm text-destructive">
                                No pudimos cargar los datos del usuario.
                            </p>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    void refetchDetail();
                                }}
                            >
                                Reintentar
                            </Button>
                        </div>
                    )}

                    {/* Campos personalizados del tenant. No rinde nada si no hay catálogo. */}
                    <DynamicUserFields fields={customFields} form={form} idPrefix="edit-cf" />

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                onOpenChange(false);
                            }}
                        >
                            Cancelar
                        </Button>
                        {/* Sin el detalle, guardar pisaría los valores actuales con los de la fila. */}
                        <Button type="submit" disabled={!detail || updateMut.isPending}>
                            Guardar
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
